/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  CircularProgress,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getAmins, getAvailableS10Users, modifyAmin, saveAmin } from '../api/employeePortal';
import MySnackbar from './MySnackbar';

export interface Admin {
  employeeCode: string;
  name: string;
  email: string;
  canManageManuals: boolean;
  canManageAdmins: boolean;
  isActive: boolean;
}

interface SecurityUser {
  SecurityUserId: number;
  UserName: string;
  Name: string;
  IsActive: boolean;
}

const AdminUsersDialog = ({ open, onClose, onSubmit, userIsAdmin }: any) => {
  const [mode, setMode] = useState<'list' | 'add' | 'edit'>('list');
  const [usersAmins, setUsersAmins] = useState([]);
  const [availableUsers, setAvailableUsers] = useState<SecurityUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<SecurityUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formData, setFormData] = useState<Admin>({
    employeeCode: '',
    name: '',
    email: '',
    canManageManuals: false,
    canManageAdmins: false,
    isActive: true
  });

  useEffect(() => {
    if (open) {
      handleGetAdmins();
    }
  }, [open]);

  useEffect(() => {
    if (mode === 'add' && availableUsers.length === 0) {
      fetchAvailableUsers();
    }
  }, [mode]);

  const handleGetAdmins = async () => {
    const result = await getAmins(userIsAdmin.employeeCode);
    setUsersAmins(result?.data?.users || []);
  };

  const fetchAvailableUsers = async () => {
    setLoadingUsers(true);
    const response = await getAvailableS10Users();
    if (response?.data) {
      setAvailableUsers(response.data);
      setFilteredUsers(response.data);
    } else {
      setErrorMsg('Error al cargar la lista de usuarios disponibles');
      console.error('Error fetching users:', response);
    }
    setLoadingUsers(false);
  };

  const handleAddClick = () => {
    setMode('add');
    setFilteredUsers(availableUsers);
    setFormData({
      employeeCode: '',
      name: '',
      email: '',
      canManageManuals: false,
      canManageAdmins: false,
      isActive: true
    });
  };

  const handleEditClick = (user: any) => {
    setMode('edit');
    setFormData({
      employeeCode: user.employeeCode,
      name: user.name,
      email: user.email,
      canManageManuals: user.canManageManuals,
      canManageAdmins: user.canManageAdmins,
      isActive: user.isActive
    });
  };

  const handleBackToList = () => {
    setFilteredUsers(availableUsers)
    setMode('list');
    setFormData({
      employeeCode: '',
      name: '',
      email: '',
      canManageManuals: false,
      canManageAdmins: false,
      isActive: true
    });
  };

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUserSearch = (searchText: string) => {
    if (!searchText) {
      setFilteredUsers(availableUsers);
      return;
    }

    const filtered = availableUsers.filter(user =>
      user.Name.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const handleUserSelect = (user: SecurityUser | null) => {
    if (!user) {
      setFormData({
        employeeCode: '',
        name: '',
        email: '',
        canManageManuals: false,
        canManageAdmins: false,
        isActive: true
      });
      return;
    }

    setFormData({
      employeeCode: user.SecurityUserId.toString(),
      name: user.Name,
      email: user.UserName,
      canManageManuals: false,
      canManageAdmins: false,
      isActive: true
    });
  };

  const isUserAlreadyAdmin = (userId: number) => {
    return usersAmins.some((admin: any) => admin.employeeCode === userId.toString());
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    let result: any;
    if (mode === 'add') {
      result = await saveAmin(userIsAdmin.employeeCode, formData);
    } else if (mode === 'edit') {
      result = await modifyAmin(userIsAdmin.employeeCode, formData);
    }
    if (result) {
      handleGetAdmins();
      if (result?.success) {
        setSuccessMsg(result?.message);
        setFormData({
          employeeCode: '',
          name: '',
          email: '',
          canManageManuals: false,
          canManageAdmins: false,
          isActive: true
        });
      } else {
        setErrorMsg(result?.message || 'Ocurrió un error al guardar los cambios.');
      }
    }
    handleBackToList();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {mode === 'list' ? (
        <>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Administradores</Typography>
              {userIsAdmin.canManageAdmins && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                >
                  Agregar Administrador
                </Button>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Gestiona Manuales</TableCell>
                  <TableCell>Gestiona Admins</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersAmins.map((user: any) => (
                  <TableRow key={user.employeeCode}>
                    <TableCell>{user.employeeCode}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.canManageManuals ? 'Sí' : 'No'}</TableCell>
                    <TableCell>{user.canManageAdmins ? 'Sí' : 'No'}</TableCell>
                    <TableCell>{user.isActive ? 'Activo' : 'Inactivo'}</TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton 
                          onClick={() => handleEditClick(user)}
                          disabled={!userIsAdmin.canManageAdmins}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cerrar</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            {mode === 'add' ? 'Agregar Administrador' : 'Editar Administrador'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {['add', 'edit'].includes(mode) && (
                <>
                  {mode === 'add' && <Autocomplete
                    options={filteredUsers}
                    getOptionLabel={(option) => option.Name}
                    filterOptions={(options) => options} // We already filter manually
                    onChange={(_, value) => handleUserSelect(value)}
                    onInputChange={(_, value) => handleUserSearch(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar usuario"
                        margin="normal"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div>
                          <div>{option.Name}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>{option.UserName}</div>
                          {isUserAlreadyAdmin(option.SecurityUserId) && (
                            <Chip 
                              label="Ya es administrador" 
                              size="small" 
                              style={{ marginLeft: '8px' }}
                              color="info"
                            />
                          )}
                        </div>
                      </li>
                    )}
                    getOptionDisabled={(option) => isUserAlreadyAdmin(option.SecurityUserId)}
                    noOptionsText="No se encontraron usuarios"
                    loading={loadingUsers}
                  />}
                  <TextField
                    fullWidth
                    label="Código de Empleado"
                    name="employeeCode"
                    value={formData.employeeCode}
                    margin="normal"
                    required
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    name="name"
                    value={formData.name}
                    margin="normal"
                    required
                    disabled
                  />
                  
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    margin="normal"
                    required
                    disabled
                  />
                </>
              )}
              
              <FormControlLabel
                control={
                  <Checkbox
                    name="canManageManuals"
                    checked={formData.canManageManuals}
                    onChange={handleChange}
                  />
                }
                label="Puede gestionar manuales"
              />
              
              {userIsAdmin.canManageAdmins && (
                <FormControlLabel
                  control={
                    <Checkbox
                      name="canManageAdmins"
                      checked={formData.canManageAdmins}
                      onChange={handleChange}
                    />
                  }
                  label="Puede gestionar administradores"
                />
              )}
              
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                }
                label="Usuario activo"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleBackToList}>Cancelar</Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={mode === 'add' && (!formData.name || !formData.email)}
              >
                {mode === 'add' ? 'Agregar' : 'Guardar Cambios'}
              </Button>
            </DialogActions>
          </form>
        </>
      )}
      
      <MySnackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        msg={errorMsg}
        severity="error"
        onClose={() => setErrorMsg('')}
      />
      <MySnackbar
        open={!!successMsg}
        autoHideDuration={6000}
        msg={successMsg}
        severity="success"
        onClose={() => setSuccessMsg('')}
      />
    </Dialog>
  );
};

export default AdminUsersDialog;